import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  static #historySectionsRefreshed = new Set();
  // Quantity and discount writers share a turn through rendering, so a late
  // discount response cannot overwrite a newer quantity (or the reverse).
  static #mutationTail = Promise.resolve();
  static #pendingCartMutations = new Set();
  /** Save quantity intentions already entered before a discount can change line keys. */
  static flushPendingQuantities() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .filter((cart) => cart instanceof CartItemsComponent && (cart.#quantityEdits.size || cart.#quantityDrain))
      .map((cart) => cart.#flushQuantityEdits());
  }

  /** Guard the whole accepted discount, including its wait for older quantity edits. */
  static runDiscountMutation(work) {
    const earlierQuantities = CartItemsComponent.flushPendingQuantities();
    const operation = earlierQuantities.length
      ? Promise.all(earlierQuantities).then(() => CartItemsComponent.runCartMutation(work))
      : CartItemsComponent.runCartMutation(work);
    CartItemsComponent.#pendingCartMutations.add(operation);
    const settled = () => { CartItemsComponent.#pendingCartMutations.delete(operation); };
    operation.then(settled, settled);
    return operation;
  }

  /** @param {() => Promise<any>} work */
  static runCartMutation(work) {
    const operation = CartItemsComponent.#mutationTail.then(work);
    CartItemsComponent.#pendingCartMutations.add(operation);
    const settled = () => { CartItemsComponent.#pendingCartMutations.delete(operation); };
    CartItemsComponent.#mutationTail = operation.then(settled, settled);
    return operation;
  }

  #drawerRefreshVersion = 0;
  #drawerRefreshRequest = 0;
  /** @type {Set<Promise<void>>} */
  #pendingCartChanges = new Set();
  // Retain one latest intent per line; a single debounced event loses other rows.
  #quantityEdits = new Map();
  #quantityGeneration = 0;
  #quantityDrain = null;
  #quantityBatch = null;
  #debouncedQuantityFlush = debounce(() => this.#flushQuantityEdits(), 300);
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    this.#trackCartChange(event);
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    this.#trackCartChange(event);
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.addEventListener('visibilitychange', this.#handleDrawerVisibility);
    window.addEventListener('pageshow', this.#handleDrawerHistory);
    if (document.readyState === 'complete') this.#handleDrawerHistory();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.removeEventListener('visibilitychange', this.#handleDrawerVisibility);
    window.removeEventListener('pageshow', this.#handleDrawerHistory);
    this.#drawerRefreshVersion++;
    this.#drawerRefreshRequest++;
    this.#quantityGeneration++;
    this.#debouncedQuantityFlush.cancel?.();
    this.#quantityEdits.clear();
    this.#quantityBatch?.resolve();
    this.#quantityBatch = null;
  }

  #trackCartChange(event) {
    this.#drawerRefreshVersion++;
    if (!event.promise) return;
    let pending;
    const settled = () => {
      this.#pendingCartChanges.delete(pending);
      this.#drawerRefreshVersion++;
    };
    pending = Promise.resolve(event.promise).then(settled, settled);
    this.#pendingCartChanges.add(pending);
  }

  #handleDrawerOpen = (event) => {
    if (!this.isDrawer || event.target !== this.closest('theme-drawer')) return;
    this.#refreshDrawer();
  };

  #handleDrawerVisibility = () => {
    if (document.visibilityState !== 'visible' || !this.closest('theme-drawer')?.hasAttribute('open')) return;
    this.#refreshDrawer();
  };

  #handleDrawerHistory = (event) => {
    if (!this.isDrawer || !this.dataset.sectionId) return;
    const historyLoad = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    if (!event?.persisted && (!historyLoad || CartItemsComponent.#historySectionsRefreshed.has(this.sectionId))) return;
    CartItemsComponent.#historySectionsRefreshed.add(this.sectionId);
    this.#refreshDrawer();
  };

  async #refreshDrawer() {
    if (!this.isDrawer || !this.dataset.sectionId) return;
    const request = ++this.#drawerRefreshRequest;
    const ownsRequest = () => this.isConnected && request === this.#drawerRefreshRequest;
    while (ownsRequest()) {
      // Keep pending quantity, discount and note controls intact until their
      // existing event promises settle. Refreshing never repeats the mutation.
      while ((this.#pendingCartChanges.size || CartItemsComponent.#pendingCartMutations.size) && ownsRequest()) {
        // Accepted writers count before their queued turn dispatches its event.
        await Promise.allSettled([...this.#pendingCartChanges, ...CartItemsComponent.#pendingCartMutations]);
      }
      if (!ownsRequest()) return;
      const version = this.#drawerRefreshVersion;
      const stillCurrent = () => ownsRequest() && version === this.#drawerRefreshVersion;
      // Hydration replaces the whole drawer inner subtree. Keep a current local
      // discount error visible when a follow-up read updates its prices and rows.
      const discountError = this.querySelector('[ref="cartDiscountError"]');
      const errorRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorConnection'];
      const errorState = discountError && !discountError.classList.contains('hidden')
        ? errorRefs.map((ref) => ({ ref, hidden: this.querySelector(`[ref="${ref}"]`)?.classList.contains('hidden') }))
        : [];
      try {
        const html = await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: 'hydration',
          injectStylesheet: this.querySelector('[data-cart-drawer-empty]') !== null,
          shouldRender: stillCurrent,
        });
        if (!ownsRequest()) return;
        // A mutation that began during this read invalidated it. Once that
        // mutation settles, finish the requested refresh without replaying it.
        if (version !== this.#drawerRefreshVersion) continue;
        if (typeof html !== 'string' || !html.trim()) return;
        for (const { ref, hidden } of errorState) {
          if (typeof hidden === 'boolean') this.querySelector(`[ref="${ref}"]`)?.classList.toggle('hidden', hidden);
        }
        const notice = this.querySelector('[data-cart-refresh-error]');
        if (notice?.contains(document.activeElement)) {
          this.closest('theme-drawer')?.querySelector('.theme-drawer__close-button')?.focus();
        }
        notice?.remove();
        this.#updateCartQuantitySelectorButtonStates();
        const empty = this.querySelector('[data-cart-drawer-empty]') !== null;
        const rawCount = this.querySelector('[ref="cartItemCount"]')?.textContent;
        const count = empty ? 0 : Number(rawCount?.trim() || NaN);
        if (Number.isInteger(count) && count >= 0) {
          for (const element of document.querySelectorAll('cart-icon')) {
            const icon = /** @type {HTMLElement & { renderCartBubble?: (count: number, animate?: boolean) => Promise<void> }} */ (element);
            if (typeof icon.renderCartBubble !== 'function') continue;
            Promise.resolve(icon.renderCartBubble(count, false)).catch((error) => {
              console.warn('[cart-items] Refreshed count could not be displayed:', error);
            });
          }
        }
        const drawer = /** @type {HTMLElement & { refresh?: () => void } | null} */ (this.closest('cart-drawer-component'));
        drawer?.refresh?.();
        return;
      } catch (error) {
        if (!ownsRequest()) return;
        if (version !== this.#drawerRefreshVersion) continue;
        if (error?.name === 'AbortError') return;
        this.#showDrawerRefreshError();
        this.dispatchEvent(new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        }));
        return;
      }
    }
  }

  #showDrawerRefreshError(messageText = "We couldn't refresh your basket. Please try again before checking out.") {
    if (this.querySelector('[data-cart-refresh-error]')) return;
    const notice = document.createElement('div');
    notice.dataset.cartRefreshError = '';
    notice.className = 'cart-drawer__items';
    notice.setAttribute('role', 'alert');
    notice.style.flex = '0 0 auto';
    notice.style.paddingBlock = 'var(--padding-md, 16px)';
    const message = document.createElement('p');
    message.textContent = messageText;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button-secondary';
    retry.textContent = 'Try again';
    retry.addEventListener('click', async () => {
      retry.disabled = true;
      try {
        if (this.isDrawer) await this.#refreshDrawer();
        else window.location.reload();
      } finally {
        retry.disabled = false;
      }
    });
    notice.append(message, retry);
    this.prepend(notice);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    // Filter before debouncing: product selectors must not cancel basket edits.
    if (!(event instanceof QuantitySelectorUpdateEvent) ||
        !(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    this.#queueQuantityEdit(line, quantity, quantity === 0 ? 'clear' : 'change');
    if (quantity === 0) this.#flushQuantityEdits();
    else this.#debouncedQuantityFlush();
  };

  /** @param {number} line */
  onLineItemRemove(line) {
    this.#queueQuantityEdit(line, 0, 'clear');
    return this.#flushQuantityEdits();
  }

  #queueQuantityEdit(line, quantity, action) {
    this.updatedCallback();
    const row = this.refs.cartItemRows?.[line - 1];
    const key = row?.dataset.key;
    if (!key) return;
    this.#quantityEdits.set(key, { key, quantity, action });
    row.querySelector('text-component')?.shimmer();
    if (!this.#quantityBatch) {
      let resolve;
      const promise = new Promise((done) => { resolve = done; });
      this.#quantityBatch = { promise, resolve };
      // This internal promise also protects unsent edits during the 300 ms delay.
      // Only real cart requests emit commerce events.
      this.#trackCartChange({ promise });
    }
  }

  #flushQuantityEdits() {
    this.#debouncedQuantityFlush.cancel?.();
    if (this.#quantityDrain) return this.#quantityDrain;
    if (!this.#quantityEdits.size) return Promise.resolve();
    const generation = this.#quantityGeneration;
    const batch = this.#quantityBatch;
    this.#quantityDrain = (async () => {
      while (generation === this.#quantityGeneration && this.#quantityEdits.size) {
        const [key, edit] = this.#quantityEdits.entries().next().value;
        this.#quantityEdits.delete(key);
        this.updatedCallback();
        const line = (this.refs.cartItemRows ?? []).findIndex((row) => row.dataset.key === key) + 1;
        // A removal or discount can change rows/keys. Never apply to a new occupant
        // of an old line number, or re-add a removed line by variant ID.
        const saved = line ? await this.updateQuantity({ ...edit, line }) : false;
        if (generation !== this.#quantityGeneration) break;
        if (!saved) {
          const hadRemainingEdits = this.#quantityEdits.size > 0;
          this.#quantityEdits.clear();
          for (const selector of this.refs.quantitySelectors ?? []) {
            const input = selector.querySelector('input');
            if (input) input.value = input.defaultValue;
          }
          resetShimmer(this);
          if (!line || hadRemainingEdits) {
            this.#showDrawerRefreshError("We couldn't save all your basket changes. Please check the quantities and try again.");
          }
          if (!line) this.dispatchEvent(new CartErrorEvent({ error: 'Basket item changed before its quantity could be saved', code: 'INVALID' }));
          break;
        }
      }
    })().finally(() => {
      this.#quantityDrain = null;
      batch?.resolve();
      if (this.#quantityBatch === batch) this.#quantityBatch = null;
      // A reconnect may have accepted fresh edits while the old request settled.
      if (this.#quantityEdits.size) this.#flushQuantityEdits();
    });
    return this.#quantityDrain;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - Stable identity captured before earlier rows change.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    return CartItemsComponent.runCartMutation(async () => {
      if (this.isConnected === false) return false;
      if (config.key) {
        this.updatedCallback();
        const line = (this.refs.cartItemRows ?? []).findIndex((row) => row.dataset.key === config.key) + 1;
        if (!line) {
          this.#showDrawerRefreshError("We couldn't save all your basket changes. Please check the quantities and try again.");
          this.dispatchEvent(new CartErrorEvent({ error: 'Basket item changed before its quantity could be saved', code: 'INVALID' }));
          return false;
        }
        config = { ...config, line };
      }
      return this.#saveQuantity(config);
    });
  }

  #saveQuantity(config) {
    // A drawer can be filled before its deferred observer starts watching.
    // Refresh refs synchronously before disabling controls or reading a line.
    this.updatedCallback();

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(config.key ? { id: config.key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    this.refs[`cartItemErrorContainer-${line}`]?.classList.add('hidden');
    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows?.[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const parsedResponseText = JSON.parse(await response.text());

        resetShimmer(this);

        if (parsedResponseText?.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return false;
        }

        const sectionHtml = parsedResponseText?.sections?.[this.sectionId];
        if (!response.ok || !Array.isArray(parsedResponseText?.items) ||
            typeof sectionHtml !== 'string' || !sectionHtml.trim()) {
          throw new Error('Cart update could not be confirmed');
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // Keep the current discount result through the following quantity redraw.
        const discountError = this.querySelector('[ref="cartDiscountError"]');
        const errorState = discountError && !discountError.classList.contains('hidden')
          ? ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorConnection'].map((ref) => ({
              ref, hidden: this.querySelector(`[ref="${ref}"]`)?.classList.contains('hidden'),
            }))
          : [];
        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        for (const { ref, hidden } of errorState) {
          if (typeof hidden === 'boolean') this.querySelector(`[ref="${ref}"]`)?.classList.toggle('hidden', hidden);
        }

        this.#updateCartQuantitySelectorButtonStates();

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });
        return true;
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.#handleCartError(line, {
          errors: Theme.translations.cart_update_error,
        }, 'SERVICE_UNAVAILABLE');
        return false;
      })
      .finally(() => {
        resetShimmer(this);
        this.#updateCartQuantitySelectorButtonStates();
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText, code = 'INVALID') => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) cartItemError.textContent = parsedResponseText.errors;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.warn('[cart-items] No error row for line', line, parsedResponseText.errors);
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code,
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    this.#trackCartChange(event);
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

// Browser history can restore the original basket HTML after a discount or
// quantity change. Revalidate the full basket page so its totals, controls and
// header all agree with Shopify. Reloads are reads and do not repeat mutations.
const cartHistoryNavigation = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
let cartHistoryReloadRequested = false;
const refreshRestoredCartPage = (event) => {
  if (cartHistoryReloadRequested || (!event?.persisted && !cartHistoryNavigation)) return;
  if (!document.querySelector('cart-items-component:not([data-drawer])')) return;
  cartHistoryReloadRequested = true;
  window.location.reload();
};
window.addEventListener('pageshow', refreshRestoredCartPage);
if (document.readyState === 'complete') refreshRestoredCartPage();
